import { createContext, useState, FC, ReactNode, useContext, useCallback } from "react";
import { AuthContext } from "./AuthContext";
import { getAllTickets } from "../utilities/api/adminService";

interface TicketsContextValue {
  ticketsData: any[];
  totalPages: number;
  currentPage: number;
  perPage: number;
  totalItems: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  loading: boolean;
  fetchTicketsData: (page?: number, filters?: any) => Promise<void>;
  setPerPage: (perPage: number) => void;
}

export const TicketsContext = createContext<TicketsContextValue>({
  ticketsData: [],
  totalPages: 1,
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  hasPreviousPage: false,
  hasNextPage: false,
  loading: false,
  fetchTicketsData: async () => {},
  setPerPage: () => {},
});

export const TicketsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { accessToken } = useContext(AuthContext);
  const [ticketsData, setTicketsData] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchTicketsData = useCallback(
    async (page: number = 1, filters?: any) => {
      if (!accessToken) return;
      try {
        setLoading(true);
        const response = await getAllTickets(accessToken, page, perPage, filters);
        if (response.ok) {
          setTicketsData(response.data.data);
          setTotalItems(response.data.total);
          setTotalPages(response.data.last_page);
          setCurrentPage(response.data.current_page);
          setHasPreviousPage(response.data.current_page > 1);
          setHasNextPage(response.data.current_page < response.data.last_page);
        }
      } catch (error: any) {
        console.error("Error fetching tickets data:", error);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, perPage]
  );

  return (
    <TicketsContext.Provider
      value={{
        ticketsData,
        totalPages,
        currentPage,
        perPage,
        totalItems,
        hasPreviousPage,
        hasNextPage,
        loading,
        fetchTicketsData,
        setPerPage,
      }}
    >
      {children}
    </TicketsContext.Provider>
  );
};

export const useTickets = () => {
  const context = useContext(TicketsContext);
  if (!context) {
    throw new Error("useTickets must be used within a TicketsProvider");
  }
  return context;
};
